import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";

import { 
  Breadcrumbs, Button, Box, 
  Collapse,
  FormControl, FormControlLabel,
  InputAdornment, IconButton,
  OutlinedInput,
  Switch,
  Typography, TextField,
  useTheme 
} from "@mui/material";

import styled from "styled-components";

import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { 
  useGetTableData, useGetTableDataInQueryString, 
  usePutTableData,
  useDeleteTableData 
} from "../../../components/Table/hooks";
import { 
  registerStatusColumns, 
  registerStatusColumns_all, 
} from "../../../constants/columns";

import { NewTableTemplate } from "../../../components/Table/TableTemplate";
import { ModalTemplate } from "../../../components/Table/ModalTemplate";
import Buttons from "../../../../common/Buttons";
import { 
  BreadcrumbsTable,
  ConfirmDialog,
  SnackbarAlert,
  formatDateWithDayjs, formatDateToDayjs,
} from "../../../components/common/HelperFunc";
import * as message from "../../../constants/messages";
import { FONT_PROPS, COLORS } from "../../../styles/colors";

//////////////////////////////////
// 스타일
//////////////////////////////////

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px 32px 16px 32px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

//////////////////////////////////
// 유해위험요인 등록 현황 컴포넌트
//////////////////////////////////
const PARENT_TITLE = "등록관리";
const CHILD_TITLE = "위험요인 등록 현황";
export function RegisterStatus() {
  const { group_code, project_code } = useParams();
  const theme = useTheme();

  const FILTER_STATUS = Object.freeze({ // 필터 상태
    ALL: "all",
    REGISTERED: "registered",
    DELETED: "deleted",
  });

  ////////////////////////
  // CRUD 요청
  ////////////////////////
  // 데이터 조회에 필요한 조건을 상태로 관리합니다.
  const [searchStatus, setSearchStatus] = useState({ filter: FILTER_STATUS.ALL });
  // 데이터 조회에 필요한 쿼리스트링을 만듭니다.
  const qeuryString = { 
    params: { // 쿼리스트링 만들때 params 이름을 사용하는게 규칙입니다.
      filter: searchStatus.filter,
    },
  }
  // 데이터 조회 요청
  const getData = useGetTableDataInQueryString(
    "request",
    "/getRegisterStatus", 
    group_code,
    project_code,
    qeuryString,
  );
  // 데이터 수정 요청
  const putData = usePutTableData(
    "request",
    "/putRegisterStatus",
    group_code,
    project_code
  );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableData(
    "request",
    "/deleteRegister",
    group_code,
    project_code
  );

  // 데이터 업데이트 알람 상태 관리
  const [dataAlert, setDataAlert] = useState({
    isOpen: false,
    message: '',
    severity:'' // 'success' | 'error' | 'warning' | 'info'
  });
  const openDataAlert = (message, severity = 'success') => {
    setDataAlert({
      ...dataAlert,
      isOpen: true,
      message: message,
      severity: severity
    });
  };
  const closeDataAlert = () => {
    setDataAlert({
      ...dataAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 데이터 가공 (삭제된 항목은 배경색 변경)
  const [tempData, setTempData] = useState([]);
  useEffect(() => {
    // 데이터 가공
    const data = getData.data.map(item => {
      const isDeleted = item.register_cause_status === 1 
        || item.process_status === 1
        || item.register_asset_status === 1;
      return {
        ...item,
        __bgColor: isDeleted
          ? {
            backgroundColor: "#ffebeb", // 연한 빨간색 배경
            backgroundImage:
              "repeating-linear-gradient(45deg, #ffcccc 0, #ffcccc 2px, transparent 2px, transparent 6px)",
            backgroundSize: "8px 8px",
          }
          : theme.palette.background.paper,
      };
    });

    setTempData(data);
  },[getData.data]);

  // 테이블 참조
  const tableRef = useRef();
  const clearSelection = () => { tableRef.current?.clearSelection(); };
  const getSelectedRows = () => { return tableRef.current?.getSelectedRows(); };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const [showAdminMode, setShowAdminMode] = useState(false); // 관리자 모드 활성화 여부
  const filters = [
    { label: "전체", value: FILTER_STATUS.ALL },
    { label: "유지 항목", value: FILTER_STATUS.REGISTERED },
    { label: "삭제 항목", value: FILTER_STATUS.DELETED },
  ];
  const customTableTopToolbar = (table) => {
    return(
      <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
        {/* 왼쪽 영역 */}
        <Box display="flex" alignItems="center" gap={2}>
          
        </Box>

        {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
        <Box display="flex" alignItems="center" gap={2}>
          {/* 검색창 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => setShowSearch((prev) => !prev)}>
              <SearchIcon />
            </IconButton>
          
            <Collapse in={showSearch} orientation="horizontal">
              <TextField
                size="small"
                placeholder="검색"
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                  table.setGlobalFilter(e.target.value);
                }}
                sx={{ minWidth: 200 }}
                variant="outlined"
                slots={{
                  input: OutlinedInput,
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setGlobalFilter('');
                            table.setGlobalFilter('');
                            setShowSearch(false); // 닫기
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                }}
              />
            </Collapse>
          </Box>

          {/* 필터 */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2  }}>
            {filters.map((filter) => {
              const isActive = searchStatus.filter === filter.value;
              return (
                <Box
                  key={filter.value}
                  onClick={() => setSearchStatus({ ...searchStatus, filter: filter.value })}
                  sx={{
                    display: "flex",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "1.375rem",
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.2s ease",
                    backgroundColor: isActive
                      ? 'rgb(var(--mui-palette-primary-mainChannel) / 0.25)'
                      : "transparent",
                    color: isActive
                      ? '#7367F0'
                      : theme.palette.text.secondary,
                    "&:hover": {
                      backgroundColor: isActive
                        ? theme.palette.primary
                        : theme.palette.action.hover,
                      color: '#7367F0'
                    },
                    "&:active": {
                      transform: "scale(0.97)",
                    },
                  }}
                >
                  <Typography variant="body2" component="div" color="inherit"
                    sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 14, px: 3, py: 2, }}>
                    {filter.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

        {/* 삭제버튼 */}
        {showAdminMode && 
          <Buttons.Delete 
            onClick={openDeleteModal} // 행 삭제 로직 실행
            disabled={table.getSelectedRowModel().rows.length === 0}
            sx={{ padding: '12px 16px',}}
          >
            데이터 삭제
          </Buttons.Delete>
        }
        {/* 관리자 모드 */}
        <FormControl component="fieldset">
          <FormControlLabel
            value="top"
            control={
              <Switch color="primary" 
                checked={showAdminMode}
                onChange={(event) => {
                  setShowAdminMode(event.target.checked);
                  clearSelection();
                }}
              />
            }
            label="관리자모드"
            labelPlacement="end" // bottom,end,start,top
            sx={{marginLeft : '4px'}}
          />
        </FormControl>

        </Box>
      </Box>
    );
  };

  // 위험요인 등록 현황 수정 모달 상태 관리
  const [editModal, setEditModal] = useState({ isOpen: false, rowData: [] });
  const openEditModal = (rowData) => setEditModal({ isOpen: true, rowData: rowData});
  const closeEditModal = () => setEditModal({ isOpen: false, rowData: [] });
  // 위험요인 등록 현황 테이블의 행 수정 액션
  const customRenderRowActionContent = (rowData) => {
    return(
      <Box sx={{ width: 80, }}>
        <Buttons.Edit
          onClick={(event) =>{
            event.stopPropagation(); // 이벤트 전파 중단
            openEditModal(rowData);
          }}
          sx={{ padding: '8px 12px' }}
        />
      </Box>
    );
  };
  // DB 업데이트 (수정 데이터)
  const updateEditData = async (updateData) => {
    try {
      const data ={
        updateData: updateData,
      };

      // 데이터베이스 업데이트
      const result = await putData.mutateAsync(data);
      if(result.status === 200) {
        openDataAlert(message.updateEditSuccess);
      }
      
      // 수정 모달 초기화
      closeEditModal();
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  }

  // 삭제 모달 상태 관리
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const openDeleteModal = () => setDeleteModal({ isOpen: true });
  const closeDeleteModal = () => setDeleteModal({ isOpen: false });

  // DB 업데이트 (물리 데이터 삭제)
  const deleteRegisterCauseData = async () => {
    closeDeleteModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {

      // 선택된 행(row) 가져오기
      const selectedRows = getSelectedRows().map((row) => row.original);

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        deleteData: selectedRows
      });

      if(result.status === 200) {
        openDataAlert(message.deleteSuccess);
      }

      // 선택 상태 초기화
      clearSelection();
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    return (
      <TableContainer>
        <NewTableTemplate
          ref={tableRef}
          columns={searchStatus.filter === FILTER_STATUS.REGISTERED ? registerStatusColumns : registerStatusColumns_all}
          data={tempData}
          topToolbarContent={customTableTopToolbar}
          renderRowActionContent={customRenderRowActionContent}
          enableCustomGlobalFilter={true}
          enabledSelectionRow={showAdminMode}
          enabledSelectionAllRow={showAdminMode}
          enableEditing={showAdminMode}
          // enabledSelectionRow={false}
          // enabledSelectionAllRow={false}
          templateSX={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "0.375rem", 
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
          }}
        />
        {/* 행(row) 수정 모달 */}
        <EditRowModalAdmin
          isOpen={editModal.isOpen}
          originalRowData={editModal.rowData}
          onConfirm={updateEditData}
          onCancel={closeEditModal}
        />
        {/* 삭제 메시지 */}
        <ConfirmDialog  
          open={deleteModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={deleteRegisterCauseData}
          onCancel={closeDeleteModal}
        />
        {/* 데이터 알람 */}
        <SnackbarAlert 
          open={dataAlert.isOpen}
          onClose={closeDataAlert}
          message={dataAlert.message}
          severity={dataAlert.severity} // 'success' | 'error' | 'warning' | 'info'
          //anchorOrigin = {{vertical: "top", horizontal: "center"}}
        />
      </TableContainer>
    );
  }
    
  // RegisterStatus 렌더링
  return (
    <Box sx={{ bgcolor: 'var(--mui-palette-background-paper)',height: '100%',display: 'flex',flexDirection: 'column'}}>
      {/* 브레드크럼 + 타이틀 */}
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
}

/////////
// 테이블의 행(row) 수정 모달 - 관리자 모드
const EditRowModalAdmin = ({ isOpen, originalRowData, onConfirm, onCancel }) => {
  const theme = useTheme();

  // 유효성 검사 알람 상태 관리
  const [validationAlert, setValidationAlert] = useState({
    isOpen: false,
    message: '',
    severity: '',
  });
  const openValidationAlert = (message, severity = 'error') => {
    setValidationAlert({
      ...validationAlert,
      isOpen: true,
      message: message,
      severity: severity,
    });
  };
  const closeValidationAlert = (event, reason) => {
    if (reason === 'clickaway') return;
    setValidationAlert({
      ...validationAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 수정 행 데이터 관리
  const [editData, setEditData] = useState([]);
  const clearEditData = () => setEditData([]);
  useEffect(() =>{
    if(!originalRowData) {
      return;
    }

    const tempData = { ...originalRowData };
    tempData['register_cause_create_date'] = formatDateToDayjs(originalRowData.register_cause_create_date);
    tempData['register_cause_delete_date'] = formatDateToDayjs(originalRowData.register_cause_delete_date);

    setEditData(tempData);
  },[originalRowData])

  // 행 수정 완료 액션
  const editAction = () => {
    if(checkEmptyData(editData)) {
      openValidationAlert(message.notAllowedEmptyRegisterDate);
      return;
    } 

    // 데이터 포맷 변경
    const data = { 
      ...editData, 
      // 변경할 내용
      register_cause_create_date: formatDateWithDayjs(editData.register_cause_create_date),
      register_cause_delete_date: formatDateWithDayjs(editData.register_cause_delete_date),
      register_cause_status: editData.register_cause_delete_date ? 1 : 0, // 삭제일자가 있으면 상태 1(삭제), 없으면 0(정상)
    };

    onConfirm(data);
    clearEditData();
  };

  // 데이터 빈칸 체크
  const checkEmptyData = (data) => {
    if(!data.register_cause_create_date) { 
      return true;
    }
    return false;
  };

  // EditRowModal 렌더링
  return (
    <ModalTemplate
      isOpen={isOpen}
      handleConfirm={editAction}
      handleCancel={() => {
        clearEditData();
        onCancel();
      }}
      title="수정하기"
      cancelText="취소"
      confirmText="수정"
      containerSX={{ width: 'auto', minWidth: '10%', maxWidth: '90%', minHeight: '10%', maxHeight: '90%' }}
      contentSX={{ flexDirection: "column", gap: 2 }}
    >
      <Typography sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 16, color: theme.palette.text }}>
        위험요인과 기계/기구: {editData?.safe_machine_name || editData?.machine_name}
      </Typography>
      <Box 
        sx={{ 
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: '100%',
          height: '100%',
          flexWrap: "wrap",     // 넘치면 다음 줄로 
          overflow: 'hidden',   // 넘치는 부분 숨기기
          overflowY: "auto",    // 세로 스크롤
          gap: 1,
          p: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "0.375rem", 
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column',  width: 'auto', height: 'auto', p: 1, gap: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <DatePicker
              label={'등록일자'}
              value={editData?.register_cause_create_date || null}
              onChange={(e) => {
                setEditData({
                  ...editData,
                  register_cause_create_date: e,
                });
              }}
              slotProps={{
                field: { clearable: true },
                // textField: {
                //   fullWidth: true,
                //   sx: {
                //     marginTop: '16px',
                //     marginBottom: '8px',
                //   },
                // },
              }}
            />
            <DatePicker
              label={'삭제일자'}
              value={editData?.register_cause_delete_date || null}
              onChange={(e) => {
                setEditData({
                  ...editData,
                  register_cause_delete_date: e,
                  delete_reason: e ? editData.delete_reason : "", // 삭제일자가 비워지면 삭제사유도 초기화
                });
              }}
              slotProps={{
                field: { clearable: true },
                // textField: {
                //   fullWidth: true,
                //   sx: {
                //     marginTop: '16px',
                //     marginBottom: '8px',
                //   },
                // },
              }}
            />
          </LocalizationProvider>
          <TextField
            label={'삭제사유'}
            value={editData?.delete_reason || ""}
            fullWidth
            onChange={(e) => {
              setEditData({
                ...editData,
                delete_reason: e.target.value,
              });
            }}
          />
        </Box>
      </Box>

      {/* 유효성 검사 알람 */}
      <SnackbarAlert 
        open={validationAlert.isOpen}
        onClose={closeValidationAlert}
        message={validationAlert.message}
        severity={validationAlert.severity} // 'success' | 'error' | 'warning' | 'info'
        // anchorOrigin = {{vertical: "top", horizontal: "center"}}
        snackBarSX={{width: '80%'}}
      />  
    </ModalTemplate>
  );
}